import { POINT_KINDS } from "@/components/market-intelligence/PointKind";
import {
  gasRegionPath,
  overviewPath,
  pointPath,
  regionPath,
  type HubTab,
  type RegionTab,
} from "@/lib/market-intelligence/paths";
import { regionRoutesSurface } from "@/lib/market-intelligence/region-nav";

import { HUB_TAB_ROUTES, subViewsFor, tabAppliesTo } from "./hubs/hub-tabs";

import type {
  MarketCommodity,
  MarketHub,
  MarketRegion,
  PointKindCode,
} from "@/types/market-intelligence";

/**
 * Market Intelligence in the top-bar search: "np15", "np15 curve",
 * "comed basis", "pjm capacity", "henry hub" — each straight to the view it
 * names, from any module.
 *
 * **Pure and client-side.** Everything it searches is already in the browser
 * (the topology, the hub views, the url builders), so it answers as the user
 * types with no request and no server change. The global search's own server
 * hits (clients, deals, contacts, accounts) arrive beside it.
 *
 * **It can only offer what the page would.** Views come from `subViewsFor` and
 * `tabAppliesTo` — the same rules the hub's tabs, pills and sidebar read — so a
 * settlement-only zone is never offered Forwards, gas is never offered Hourly,
 * and Curve needs `market_intelligence.forward_curves` here exactly as it does
 * there. Region surfaces follow `regionRoutesSurface` (RECs and Capacity).
 *
 * **How a query reads.** It is split into words, and each word is one of:
 * a view word ("forward", "curve", "hourly", "basis"…), a region surface word
 * ("recs", "capacity"), a commodity word ("gas", "electric"), a filler word
 * ("hub", "price"), or an IDENTITY word — what names a point or region. Every
 * identity word must match the candidate, and at least one must match the
 * point's own name, code or curve: a word that only matches its region
 * ("pjm") finds the region, not every point in it.
 */

/** What `searchMarket` reads: the live topology, or the static fallback. */
export interface MarketSearchCatalog {
  hubs: readonly MarketHub[];
  isos: readonly MarketRegion[];
  gasRegions: readonly MarketRegion[];
}

/** The caller's resolved permissions — Curve needs its own. */
export interface MarketSearchAccess {
  granted: readonly string[];
  is_admin: boolean;
}

export type MarketSearchIcon =
  | { type: "point"; kind: PointKindCode }
  | { type: "region" }
  | { type: "overview" };

export interface MarketSearchHit {
  /** Unique within one result list. */
  key: string;
  /** The point or region's own name ("NP15", "PJM"). */
  label: string;
  /** The view it opens ("Forwards › Curve"); null for the point or region's own page. */
  view: string | null;
  /** Context for the sub-line ("CAISO · Hub · Electric"). */
  detail: string;
  path: string;
  commodity: MarketCommodity;
  icon: MarketSearchIcon;
}

type HubTabKey = "forwards" | "index";

/** A view a word asks for; `view: null` = the tab's first view. */
interface ViewWant {
  tab: HubTabKey;
  view: string | null;
}

const VIEW_WORDS: Record<string, ViewWant> = {
  forward: { tab: "forwards", view: null },
  forwards: { tab: "forwards", view: null },
  fwd: { tab: "forwards", view: null },
  fwds: { tab: "forwards", view: null },
  strip: { tab: "forwards", view: "strips" },
  strips: { tab: "forwards", view: "strips" },
  curve: { tab: "forwards", view: "curve" },
  curves: { tab: "forwards", view: "curve" },
  index: { tab: "index", view: null },
  indexes: { tab: "index", view: null },
  indices: { tab: "index", view: null },
  settle: { tab: "index", view: null },
  settles: { tab: "index", view: null },
  settled: { tab: "index", view: null },
  settlement: { tab: "index", view: null },
  settlements: { tab: "index", view: null },
  spot: { tab: "index", view: null },
  trend: { tab: "index", view: "trends" },
  trends: { tab: "index", view: "trends" },
  hourly: { tab: "index", view: "hourly" },
  hour: { tab: "index", view: "hourly" },
  hours: { tab: "index", view: "hourly" },
  lmp: { tab: "index", view: "hourly" },
  lmps: { tab: "index", view: "hourly" },
  intraday: { tab: "index", view: "hourly" },
  basis: { tab: "index", view: "basis" },
};

/** A region's surfaces, by the words that ask for them. Applied to regions only. */
const REGION_TAB_WORDS: Record<string, RegionTab> = {
  rec: "recs",
  recs: "recs",
  certificate: "recs",
  certificates: "recs",
  capacity: "capacity",
  auction: "capacity",
  rpm: "capacity",
  hubs: "hubs",
  zones: "load-zones",
  citygate: "delivery-points",
  citygates: "delivery-points",
};

const REGION_TAB_LABEL: Record<RegionTab, string> = {
  hubs: "Hubs",
  "load-zones": "Load zones",
  "delivery-points": "Delivery points",
  recs: "RECs",
  capacity: "Capacity",
};

const COMMODITY_WORDS: Record<string, MarketCommodity> = {
  gas: "gas",
  natgas: "gas",
  power: "power",
  electric: "power",
  electricity: "power",
  elec: "power",
};

const COMMODITY_LABEL: Record<MarketCommodity, string> = {
  power: "Electric",
  gas: "Gas",
};

/** The module's own name: alone, it opens the Overview. */
const MODULE_WORDS = new Set(["market", "markets", "intelligence", "mi"]);

/** Words that name nothing: dropped from the identity words. */
const FILLER_WORDS = new Set([
  "hub",
  "zone",
  "load",
  "point",
  "points",
  "delivery",
  "price",
  "prices",
  "pricing",
  "chart",
  "view",
  "data",
  "the",
  "of",
  "at",
  "for",
  "in",
]);

/** Lowercase words; any run of non-alphanumerics splits. "TH_NP15_GEN-APND" → th np15 gen apnd. */
function words(text: string | null | undefined): string[] {
  return (text ?? "")
    .toLowerCase()
    .split(/[^a-z0-9]+/)
    .filter(Boolean);
}

interface Field {
  words: string[];
  /** The field's words run together, for "np 15" against "np15". */
  compact: string;
  weight: number;
  /** Whether a match here names the candidate itself, not just its region. */
  identity: boolean;
}

function field(ws: string[], weight: number, identity: boolean): Field | null {
  return ws.length === 0 ? null : { words: ws, compact: ws.join(""), weight, identity };
}

function tokenScore(token: string, f: Field): number {
  if (f.words.includes(token)) return f.weight;
  if (token.length >= 2 && f.words.some((w) => w.startsWith(token))) return f.weight * 0.7;
  if (token.length >= 3 && f.compact.includes(token)) return f.weight * 0.4;
  return 0;
}

/**
 * Every token must match some field, and at least one an identity field;
 * `null` otherwise. Typing the whole name, spaced or not ("np 15", "com ed"),
 * earns a bonus, so an exact name outranks a prefix of a longer one.
 */
function score(tokens: string[], fields: Field[]): number | null {
  let total = 0;
  let named = false;
  for (const token of tokens) {
    let best = 0;
    let bestIsIdentity = false;
    for (const f of fields) {
      const s = tokenScore(token, f);
      if (s > best) {
        best = s;
        bestIsIdentity = f.identity;
      }
    }
    if (best === 0) return null;
    total += best;
    named ||= bestIsIdentity;
  }
  if (!named) return null;
  const compactQuery = tokens.join("");
  if (fields.some((f) => f.identity && f.compact === compactQuery)) total += 100;
  return total;
}

function pointFields(hub: MarketHub): Field[] {
  const region = new Set(words(hub.iso));
  // The region's own words are context, not identity: "PJM ComEd Financial"
  // must not make "pjm" name ComEd.
  const own = (text: string | null) => words(text).filter((w) => !region.has(w));
  return [
    field(words(hub.name), 100, true),
    field(own(hub.code), 80, true),
    field(own(hub.curveLocation), 40, true),
    field(own(hub.indexNode), 30, true),
    field([...region], 20, false),
  ].filter((f): f is Field => f !== null);
}

function regionFields(region: MarketRegion): Field[] {
  return [field(words(region.code), 90, true), field(words(region.name), 50, true)].filter(
    (f): f is Field => f !== null,
  );
}

/** One applicable, permitted view of a hub, with what the row calls it. */
interface HubView {
  tab: HubTabKey;
  view: string;
  label: string;
  path: string;
}

function viewsOf(hub: MarketHub, parent: MarketHub | undefined, access: MarketSearchAccess): HubView[] {
  const permissions = { granted: [...access.granted], is_admin: access.is_admin };
  return HUB_TAB_ROUTES.flatMap((t) => {
    const tab = t.path as HubTabKey;
    if (!tabAppliesTo(tab, hub)) return [];
    return subViewsFor(tab, hub, permissions, parent).map((v) => ({
      tab,
      view: v.path,
      label: `${t.label} › ${v.label}`,
      path: `${pointPath(hub, tab as HubTab)}/${v.path}`,
    }));
  });
}

function pointHit(hub: MarketHub, view: HubView | null): MarketSearchHit {
  return {
    key: `point:${hub.code}:${view ? `${view.tab}/${view.view}` : "page"}`,
    label: hub.name,
    view: view?.label ?? null,
    detail: [hub.iso, POINT_KINDS[hub.kind].label, COMMODITY_LABEL[hub.commodity]].join(" · "),
    path: view?.path ?? pointPath(hub),
    commodity: hub.commodity,
    icon: { type: "point", kind: hub.kind },
  };
}

function regionHit(
  region: MarketRegion,
  commodity: MarketCommodity,
  tab: RegionTab | null,
): MarketSearchHit {
  const path = commodity === "gas" ? gasRegionPath(region.code, tab ?? undefined) : regionPath(region.code, tab ?? undefined);
  return {
    key: `region:${commodity}:${region.code}:${tab ?? "page"}`,
    // The code, never the full name: this module names regions by code.
    label: region.code,
    view: tab ? REGION_TAB_LABEL[tab] : null,
    detail: `${COMMODITY_LABEL[commodity]} region`,
    path,
    commodity,
    icon: { type: "region" },
  };
}

function overviewHit(commodity: MarketCommodity): MarketSearchHit {
  return {
    key: `overview:${commodity}`,
    label: "Market Intelligence",
    view: `${COMMODITY_LABEL[commodity]} overview`,
    detail: "Overview",
    path: overviewPath(commodity),
    commodity,
    icon: { type: "overview" },
  };
}

/** Whether a region's surface exists, for the surfaces a word can ask for. */
function regionHasTab(
  region: MarketRegion,
  commodity: MarketCommodity,
  tab: RegionTab,
  hubs: readonly MarketHub[],
): boolean {
  const has = (kind: PointKindCode) =>
    hubs.some((h) => h.commodity === commodity && h.iso === region.code && h.kind === kind);
  switch (tab) {
    case "hubs":
      return has("hub");
    case "load-zones":
      return commodity === "power" && has("load_zone");
    case "delivery-points":
      return commodity === "gas" && has("delivery_point");
    case "recs":
    case "capacity":
      return commodity === "power" && regionRoutesSurface(region.code, tab);
  }
}

interface Ranked {
  score: number;
  expand: (lead: boolean) => MarketSearchHit[];
}

/** Whether the top candidate clearly beats the next, so it can be expanded into its views. */
function isClearLead(ranked: Ranked[]): boolean {
  if (ranked.length <= 1) return ranked.length === 1;
  return ranked[0].score >= ranked[1].score * 1.5;
}

/**
 * Market Intelligence destinations for a query, best first, at most `limit`.
 * Empty when nothing names a point, a region or the module.
 */
export function searchMarket(
  query: string,
  catalog: MarketSearchCatalog,
  access: MarketSearchAccess,
  limit = 8,
): MarketSearchHit[] {
  const wantViews: ViewWant[] = [];
  const wantRegionTabs = new Set<RegionTab>();
  const commodities = new Set<MarketCommodity>();
  let namesModule = false;
  const identity: string[] = [];

  for (const w of words(query)) {
    if (VIEW_WORDS[w]) wantViews.push(VIEW_WORDS[w]);
    else if (REGION_TAB_WORDS[w]) wantRegionTabs.add(REGION_TAB_WORDS[w]);
    else if (COMMODITY_WORDS[w]) commodities.add(COMMODITY_WORDS[w]);
    else if (MODULE_WORDS.has(w)) namesModule = true;
    else if (!FILLER_WORDS.has(w)) identity.push(w);
  }

  const inCommodity = (c: MarketCommodity) => commodities.size === 0 || commodities.has(c);

  if (identity.length === 0) {
    // "gas", "market intelligence": the Overview. View words alone ("curve")
    // name no point, so they find nothing rather than every point there is.
    if (!namesModule && commodities.size === 0) return [];
    const overviews: MarketCommodity[] = commodities.size > 0 ? [...commodities] : ["power", "gas"];
    return overviews.map(overviewHit).slice(0, limit);
  }

  // A view asked for by name ("curve") beats the bare tab ("forward") it sits under.
  const specific = wantViews.filter(
    (w) => w.view !== null || !wantViews.some((o) => o.tab === w.tab && o.view !== null),
  );

  const byCode = new Map(catalog.hubs.map((h) => [h.code, h]));
  const ranked: Ranked[] = [];

  for (const hub of catalog.hubs) {
    if (!inCommodity(hub.commodity)) continue;
    const s = score(identity, pointFields(hub));
    if (s === null) continue;
    ranked.push({
      score: s,
      expand: (lead) => {
        const views = viewsOf(hub, hub.parentHub ? byCode.get(hub.parentHub) : undefined, access);
        if (specific.length > 0) {
          const picked = specific
            .map(
              (want) =>
                views.find((v) => v.tab === want.tab && v.view === want.view) ??
                // Asked for a view this point doesn't offer (or this user can't
                // see): the tab's first view, honestly labelled, if the tab applies.
                views.find((v) => v.tab === want.tab),
            )
            .filter((v): v is HubView => v !== undefined);
          return picked.length > 0 ? picked.map((v) => pointHit(hub, v)) : [pointHit(hub, null)];
        }
        return lead ? [pointHit(hub, null), ...views.map((v) => pointHit(hub, v))] : [pointHit(hub, null)];
      },
    });
  }

  const regionSets: Array<[MarketCommodity, readonly MarketRegion[]]> = [
    ["power", catalog.isos],
    ["gas", catalog.gasRegions],
  ];
  for (const [commodity, regions] of regionSets) {
    if (!inCommodity(commodity)) continue;
    for (const region of regions) {
      const s = score(identity, regionFields(region));
      if (s === null) continue;
      ranked.push({
        // A region outranks a point it merely shares a word with.
        score: s + 1,
        expand: (lead) => {
          const tabs = [...wantRegionTabs].filter((t) => regionHasTab(region, commodity, t, catalog.hubs));
          if (tabs.length > 0) return tabs.map((t) => regionHit(region, commodity, t));
          if (!lead) return [regionHit(region, commodity, null)];
          // The surfaces a region page doesn't already lead with.
          const extras = (["recs", "capacity"] as const).filter((t) =>
            regionHasTab(region, commodity, t, catalog.hubs),
          );
          return [regionHit(region, commodity, null), ...extras.map((t) => regionHit(region, commodity, t))];
        },
      });
    }
  }

  ranked.sort((a, b) => b.score - a.score);
  const lead = isClearLead(ranked);
  const hits: MarketSearchHit[] = [];
  const seen = new Set<string>();
  ranked.forEach((r, i) => {
    for (const hit of r.expand(i === 0 && lead)) {
      if (seen.has(hit.path)) continue;
      seen.add(hit.path);
      hits.push(hit);
    }
  });
  return hits.slice(0, limit);
}
